import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, FileDown, Send, CheckCircle2, XCircle, FileText, Info,
  Lock, Pencil, Trash2,
} from 'lucide-react';
import { useTranslation } from '../../i18n';

import { Card } from '../../components/Shared/Card';
import { Loading } from '../../components/Shared/Loading';
import { Button } from '../../components/Shared/Button';
import { EmptyState } from '../../components/Shared/EmptyState';
import { ConfirmDialog } from '../../components/Shared/ConfirmDialog';

import { useInvoiceStore } from '../../store/useInvoiceStore';
import { formatDate } from '@/utils/dateUtils';
import { formatContactPerson, toSalutation } from '@/utils/salutation';
import { formatInvoicePeriod, isStandingInvoice, toPaymentInterval } from '@/types/billing';
import { getInvoiceStatusBadge } from './statusBadge';
import { formatMoney } from '@/utils/money';
import { formatPercent } from '@/utils/percent';
import { InvoicePositionsSection } from './components/InvoicePositionsSection';
import { StandingPaymentPlan } from './components/StandingPaymentPlan';
import {
  EditInvoiceSlideOver,
  type EditInvoiceFormData,
} from './components/EditInvoiceSlideOver';
import {
  InvoicePositionModal,
  type InvoicePositionFormValues,
} from './components/InvoicePositionModal';
import type { FacilityFlowCoreModulesInvoicesDtosInvoicePositionDto } from '../../api/generated/models';

type InvoicePositionDto = FacilityFlowCoreModulesInvoicesDtosInvoicePositionDto;

type PendingAction = 'issue' | 'send' | 'pay' | 'cancel' | 'delete' | null;

export default function InvoiceDetailPage() {
  const { t } = useTranslation(['invoices', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const {
    selectedInvoice, loading, error, saving, downloadingPdf,
    getInvoiceById, issueInvoice, markSent, markPaid, cancelInvoice, deleteInvoice,
    updateInvoice, addPosition, updatePosition, deletePosition, reorderPositions,
    downloadPdf,
  } = useInvoiceStore();

  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<InvoicePositionDto | null>(null);
  const [deletingPosition, setDeletingPosition] = useState<InvoicePositionDto | null>(null);

  useEffect(() => {
    if (id) getInvoiceById(id);
  }, [id, getInvoiceById]);

  if (loading && !selectedInvoice) {
    return <Loading />;
  }

  if (error && !selectedInvoice) {
    return (
      <Card>
        <EmptyState
          variant="error"
          title={t('invoices:messages.loadError')}
          description={error}
          onRetry={() => id && getInvoiceById(id)}
        />
      </Card>
    );
  }

  if (!selectedInvoice) {
    return (
      <EmptyState
        icon={<FileText className="w-12 h-12" strokeWidth={1.75} />}
        title={t('invoices:empty.title')}
      />
    );
  }

  const inv = selectedInvoice;
  const status = (inv.status ?? '').toLowerCase();
  // why: only drafts are mutable — `Issued` is the finalized state (Api ADR-0018).
  const isDraft = status === 'draft';
  // Recipient snapshot stored on the invoice (FAFD-14) — not the current master data.
  const customerName = inv.customerName ?? '';
  const objectName = inv.objectId
    ? inv.objectName ?? ''
    : t('invoices:fields.manual');

  const period = formatInvoicePeriod(inv);
  // why: a yearly invoice is a „Dauerrechnung“ (FACF-106) — different title, PDF name and
  // an explanatory line; everything else (status flow, editing) is identical.
  const isStanding = isStandingInvoice(inv.billingInterval);
  // why: a standing invoice states one payment period, valid for 12 months from the contract
  // start (FAFD-27) — the totals read „je Monat/Quartal/…".
  const terms = isStanding ? inv.standingTerms : null;
  const standingPeriod = terms
    ? `${formatDate(terms.validFrom ?? undefined)} – ${formatDate(terms.validTo ?? undefined)}`
    : '';
  const perPeriod = terms ? ` ${t(`invoices:standingTerms.per.${toPaymentInterval(terms.interval)}`)}` : '';
  const installments = terms?.installments ?? [];
  // why: period and positions of a standing draft are fixed by its payment plan — the backend
  // rejects changes (invoices.standingDraftLocked); delete the draft and create it anew instead.
  const standingLocked = isDraft && isStanding;
  const titleKey = isStanding ? 'invoices:detail.standingTitle' : 'invoices:detail.title';
  const draftTitleKey = isStanding
    ? 'invoices:detail.standingDraftTitle'
    : 'invoices:detail.draftTitle';

  const sortedPositions = (inv.positions ?? [])
    .slice()
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  const hasPositions = sortedPositions.length > 0;

  const confirmAction = async () => {
    if (!id || !pendingAction) return;
    try {
      if (pendingAction === 'issue') await issueInvoice(id);
      else if (pendingAction === 'send') await markSent(id);
      else if (pendingAction === 'pay') await markPaid(id);
      else if (pendingAction === 'cancel') await cancelInvoice(id);
      else if (pendingAction === 'delete') {
        await deleteInvoice(id);
        navigate('/invoices');
      }
    } finally {
      setPendingAction(null);
    }
  };

  const confirmTitle = pendingAction
    ? {
        issue: t('invoices:actions.issue'),
        send: t('invoices:actions.markSent'),
        pay: t('invoices:actions.markPaid'),
        cancel: t('invoices:actions.cancel'),
        delete: t('invoices:actions.delete'),
      }[pendingAction]
    : '';

  const confirmMessage = pendingAction
    ? {
        issue: t('invoices:confirm.issue'),
        send: t('invoices:confirm.markSent'),
        pay: t('invoices:confirm.markPaid'),
        cancel: t('invoices:confirm.cancel'),
        delete: t('invoices:confirm.delete'),
      }[pendingAction]
    : '';

  /* ---------------- Draft editing handlers ---------------- */

  const handleSaveInvoice = async (form: EditInvoiceFormData) => {
    if (!inv.id) return;
    try {
      await updateInvoice(inv.id, {
        // why: UpdateInvoiceRequest requires contactPersonName, introductionText and
        // closingText as non-nullable strings; paymentTerms / buyerReference are optional.
        contactPersonName: form.contactPersonName,
        contactSalutation: form.contactSalutation,
        introductionText: form.introductionText,
        closingText: form.closingText,
        serviceDateFrom: form.serviceDateFrom,
        serviceDateTo: form.serviceDateTo,
        paymentTerms: form.paymentTerms || null,
        buyerReference: form.buyerReference || null,
      });
      setIsEditOpen(false);
    } catch {
      // toast shown by the store; keep the slide-over open for corrections
    }
  };

  const openAddPosition = () => {
    setEditingPosition(null);
    setIsPositionModalOpen(true);
  };

  const openEditPosition = (position: InvoicePositionDto) => {
    setEditingPosition(position);
    setIsPositionModalOpen(true);
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setEditingPosition(null);
  };

  const handleSavePosition = async (values: InvoicePositionFormValues) => {
    if (!inv.id) return;
    const payload = {
      title: values.title,
      description: values.description || null,
      unit: values.unit,
      quantity: values.quantity,
      unitNetPrice: values.unitNetPrice,
      vatRate: values.vatRate,
      // why: source links and service dates of an existing position stay untouched
      // server-side; for new positions the BE defaults the dates to the invoice period.
      vatCategoryCode: editingPosition?.vatCategoryCode ?? null,
      serviceDateFrom: editingPosition?.serviceDateFrom ?? null,
      serviceDateTo: editingPosition?.serviceDateTo ?? null,
    };
    try {
      if (editingPosition?.id) {
        await updatePosition(inv.id, editingPosition.id, payload);
      } else {
        await addPosition(inv.id, payload);
      }
      closePositionModal();
    } catch {
      // toast shown by the store; keep the modal open
    }
  };

  const handleDeletePosition = async () => {
    if (!inv.id || !deletingPosition?.id) return;
    try {
      await deletePosition(inv.id, deletingPosition.id);
    } finally {
      setDeletingPosition(null);
    }
  };

  const handleMovePosition = async (index: number, direction: 'up' | 'down') => {
    if (!inv.id) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sortedPositions.length) return;
    const reordered = [...sortedPositions];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    // why: the BE renumbers and therefore demands the complete id set, not a partial one.
    const positionIds = reordered.map((p) => p.id).filter(Boolean) as string[];
    try {
      await reorderPositions(inv.id, positionIds);
    } catch {
      // toast shown by the store
    }
  };

  const deletingSource = (deletingPosition?.sourceType ?? '').toLowerCase();
  const deleteMessage = deletingSource === 'recurring' || deletingSource === 'adhoc'
    ? t('invoices:positions.confirmDeleteLinked')
    : t('invoices:positions.confirmDelete');

  return (
    <div className="space-y-4 sm:space-y-6">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => navigate('/invoices')}
        leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
      >
        {t('invoices:actions.back')}
      </Button>

      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900 truncate">
                {inv.invoiceNumber
                  ? <>{t(titleKey)} <span className="font-mono-num">{inv.invoiceNumber}</span></>
                  : t(draftTitleKey)}
              </h1>
              {getInvoiceStatusBadge(inv.status, t)}
            </div>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {customerName} · {objectName} · <span className="tabular-nums">{period}</span>
            </p>
            {isStanding && (
              <p className="text-[12px] text-slate-500 mt-1">
                {t('invoices:detail.standingInfo', { period: standingPeriod })}
              </p>
            )}
            {isDraft && (
              <p className="text-[12px] text-slate-500 mt-1">
                {t(standingLocked ? 'invoices:hints.standingDraftLocked' : 'invoices:hints.draftEditable')}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {isDraft && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setIsEditOpen(true)}
                  disabled={saving}
                  leftIcon={<Pencil className="w-4 h-4" strokeWidth={2} />}
                >
                  {t('invoices:actions.edit')}
                </Button>
                {/* why: a wrapping span keeps the tooltip reachable while the button is disabled. */}
                <span title={hasPositions ? undefined : t('invoices:hints.issueNeedsPositions')}>
                  <Button
                    onClick={() => setPendingAction('issue')}
                    disabled={saving || !hasPositions}
                    leftIcon={<Lock className="w-4 h-4" strokeWidth={2} />}
                  >
                    {t('invoices:actions.issue')}
                  </Button>
                </span>
                <Button
                  variant="danger"
                  onClick={() => setPendingAction('delete')}
                  disabled={saving}
                  leftIcon={<Trash2 className="w-4 h-4" strokeWidth={2} />}
                >
                  {t('invoices:actions.delete')}
                </Button>
              </>
            )}
            {status === 'issued' && (
              <>
                <Button
                  onClick={() => setPendingAction('send')}
                  disabled={saving}
                  leftIcon={<Send className="w-4 h-4" strokeWidth={2} />}
                >
                  {t('invoices:actions.markSent')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setPendingAction('pay')}
                  disabled={saving}
                  leftIcon={<CheckCircle2 className="w-4 h-4" strokeWidth={2} />}
                >
                  {t('invoices:actions.markPaid')}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setPendingAction('cancel')}
                  disabled={saving}
                  leftIcon={<XCircle className="w-4 h-4" strokeWidth={2} />}
                >
                  {t('invoices:actions.cancel')}
                </Button>
              </>
            )}
            {status === 'sent' && (
              <>
                <Button
                  variant="primary"
                  onClick={() => setPendingAction('pay')}
                  disabled={saving}
                  leftIcon={<CheckCircle2 className="w-4 h-4" strokeWidth={2} />}
                >
                  {t('invoices:actions.markPaid')}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setPendingAction('cancel')}
                  disabled={saving}
                  leftIcon={<XCircle className="w-4 h-4" strokeWidth={2} />}
                >
                  {t('invoices:actions.cancel')}
                </Button>
              </>
            )}
            <Button
              variant="secondary"
              onClick={() => downloadPdf(inv.id ?? '', inv.invoiceNumber ?? null, inv.billingInterval)}
              disabled={downloadingPdf || !inv.id}
              leftIcon={<FileDown className="w-4 h-4" strokeWidth={2} />}
            >
              {downloadingPdf
                ? t('invoices:actions.generatingPdf')
                : t('invoices:actions.downloadPdf')}
            </Button>
          </div>
        </div>
      </div>

      {/* Metadata card */}
      <Card title={t('invoices:detail.metadata')}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field
            label={t('invoices:fields.customer')}
            value={customerName || '—'}
            onClick={inv.customerId ? () => navigate(`/customers/${inv.customerId}`) : undefined}
          />
          <Field
            label={t('invoices:fields.object')}
            value={objectName || '—'}
            onClick={inv.objectId ? () => navigate(`/objects/${inv.objectId}`) : undefined}
            italic={!inv.objectId}
          />
          <Field
            label={t('invoices:fields.contactPerson')}
            value={formatContactPerson(toSalutation(inv.contactSalutation), inv.contactPersonName, t) || '—'}
          />
          <Field
            label={t('invoices:fields.billingPeriod')}
            value={period}
          />
          <Field
            label={t('invoices:fields.serviceDateFrom')}
            value={formatDate(inv.serviceDateFrom ?? undefined)}
          />
          <Field
            label={t('invoices:fields.serviceDateTo')}
            value={formatDate(inv.serviceDateTo ?? undefined)}
          />
          <Field
            label={t('invoices:fields.issueDate')}
            value={formatDate(inv.issueDate ?? undefined)}
          />
          {/* why: a standing invoice is due once per payment period (terms below), not on one date (FAFD-27). */}
          {!isStanding && (
            <Field
              label={t('invoices:fields.dueDate')}
              value={formatDate(inv.dueDate ?? undefined)}
            />
          )}
          <Field
            label={t('invoices:fields.taxRate')}
            value={formatPercent(inv.taxRate)}
          />
          <Field
            label={t('invoices:fields.buyerVatId')}
            value={inv.buyerVatId || '—'}
          />
          <Field
            label={t('invoices:fields.supplierVatId')}
            value={inv.supplierVatId || '—'}
          />
          <Field
            label={t('invoices:fields.buyerReference')}
            value={inv.buyerReference || '—'}
          />
          <Field
            label={t('invoices:fields.supplierIban')}
            value={inv.supplierIban || '—'}
          />
          <Field
            label={t('invoices:fields.supplierBankName')}
            value={inv.supplierBankName || '—'}
          />
          <Field
            label={t('invoices:fields.currency')}
            value={inv.currency || 'EUR'}
          />
        </div>
      </Card>

      {/* Das Angebot hinter der Rechnung wurde nach dem Erzeugen geaendert. Die
          Positionen hier sind ein Schnappschuss und bleiben, wie sie sind. */}
      {inv.sourceOfferChangedAfterCreation && (
        <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={2} />
          <p className="text-[13px] text-slate-600">
            {t('invoices:hints.sourceOfferChanged')}
          </p>
        </div>
      )}

      {/* Positions */}
      <InvoicePositionsSection
        positions={sortedPositions}
        isEditable={isDraft && !standingLocked}
        saving={saving}
        onAdd={openAddPosition}
        onEdit={openEditPosition}
        onDelete={setDeletingPosition}
        onMove={handleMovePosition}
      />

      {/* Totals */}
      <Card title={t('invoices:detail.totals')}>
        <div className="ml-auto max-w-sm space-y-1.5">
          <TotalRow
            label={`${t('invoices:fields.totalNetAmount')}${perPeriod}`}
            value={formatMoney(inv.totalNetAmount, { currency: inv.currency })}
          />
          <TotalRow
            label={`${t('invoices:fields.taxRate')} (${formatPercent(inv.taxRate)})${perPeriod}`}
            value={formatMoney(inv.totalVatAmount, { currency: inv.currency })}
          />
          <div className="h-px bg-slate-200 my-2" />
          <TotalRow
            label={`${t('invoices:fields.totalGrossAmount')}${perPeriod}`}
            value={formatMoney(inv.totalGrossAmount, { currency: inv.currency })}
            bold
            primary
          />
          {terms && (
            // why: validity, interval and due dates make the standing invoice usable (FAFD-27).
            <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 space-y-0.5">
              <p className="text-[13px] font-semibold text-slate-900">
                {t(`invoices:standingTerms.payable.${toPaymentInterval(terms.interval)}`, {
                  amount: formatMoney(inv.totalGrossAmount, { currency: inv.currency }),
                })}
              </p>
              {/* why: with a payment plan the due dates are listed in the table below. */}
              {installments.length === 0 && (
                <p className="text-[11px] text-slate-500 tabular-nums">
                  {(terms.dueDates?.length ?? 0) > 4
                    ? t('invoices:standingTerms.dueRange', {
                      count: terms.periods ?? 0,
                      from: formatDate(terms.dueDates?.[0] ?? undefined),
                      to: formatDate(terms.dueDates?.[terms.dueDates.length - 1] ?? undefined),
                    })
                    : t('invoices:standingTerms.dueList', {
                      dates: (terms.dueDates ?? []).map((d) => formatDate(d)).join(', '),
                    })}
                </p>
              )}
              <p className="text-[11px] text-slate-500">{t('invoices:standingTerms.validity', { period: standingPeriod })}</p>
            </div>
          )}
        </div>
      </Card>

      {installments.length > 0 && (
        <StandingPaymentPlan installments={installments} currency={inv.currency} />
      )}

      {/* Texts — drafts always show all three cards so the editable fields are visible */}
      {(isDraft || inv.introductionText || inv.closingText || (!isStanding && inv.paymentTerms)) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(isDraft || inv.introductionText) && (
            <TextCard
              title={t('invoices:fields.introductionText')}
              text={inv.introductionText}
              placeholder={t('invoices:detail.noIntroduction')}
            />
          )}
          {(isDraft || inv.closingText) && (
            <TextCard
              title={t('invoices:fields.closingText')}
              text={inv.closingText}
              placeholder={t('invoices:detail.noClosing')}
            />
          )}
          {/* A standing invoice states its terms in the box above; the PDF leaves the text out too. */}
          {!isStanding && (isDraft || inv.paymentTerms) && (
            <TextCard
              title={t('invoices:fields.paymentTerms')}
              text={inv.paymentTerms}
              placeholder={t('invoices:detail.noPaymentTerms')}
            />
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmAction}
        title={confirmTitle}
        message={confirmMessage}
        variant={pendingAction === 'cancel' || pendingAction === 'delete' ? 'danger' : 'info'}
        loading={saving}
      />

      {isDraft && (
        <>
          <EditInvoiceSlideOver
            isOpen={isEditOpen}
            onClose={() => setIsEditOpen(false)}
            invoice={inv}
            saving={saving}
            onSave={handleSaveInvoice}
          />
          <InvoicePositionModal
            isOpen={isPositionModalOpen}
            onClose={closePositionModal}
            saving={saving}
            initial={editingPosition}
            defaultVatRate={inv.taxRate ?? 20}
            onSave={handleSavePosition}
          />
          <ConfirmDialog
            isOpen={deletingPosition !== null}
            onClose={() => setDeletingPosition(null)}
            onConfirm={handleDeletePosition}
            title={t('invoices:positions.delete')}
            message={deleteMessage}
            variant="danger"
            loading={saving}
          />
        </>
      )}
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onClick?: () => void;
  italic?: boolean;
}

function Field({ label, value, onClick, italic }: FieldProps) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </div>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="text-[13px] font-medium text-[#00A871] hover:underline text-left mt-0.5"
        >
          {value}
        </button>
      ) : (
        <div
          className={`text-[13px] mt-0.5 ${italic ? 'italic text-slate-500' : 'font-medium text-slate-900'}`}
        >
          {value}
        </div>
      )}
    </div>
  );
}

interface TextCardProps {
  title: string;
  text?: string | null;
  placeholder: string;
}

function TextCard({ title, text, placeholder }: TextCardProps) {
  return (
    <Card title={title}>
      {text ? (
        <p className="text-[13px] whitespace-pre-line text-slate-700">{text}</p>
      ) : (
        <p className="text-[13px] italic text-slate-500">{placeholder}</p>
      )}
    </Card>
  );
}

interface TotalRowProps {
  label: string;
  value: string;
  bold?: boolean;
  primary?: boolean;
}

function TotalRow({ label, value, bold, primary }: TotalRowProps) {
  const base = primary
    ? 'text-[15px] text-[#00A871]'
    : 'text-[13px] text-slate-700';
  return (
    <div className={`flex items-center justify-between ${bold ? 'font-semibold' : ''} ${base}`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
